import React, { useEffect, useState, useContext, useRef, useCallback, useMemo } from "react";
import Web3 from "web3";
import { TabView, TabPanel } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { useWeb3React } from "@web3-react/core";

import ContractContext from '../ContractContext';
import DataContext from "../DataContext";

import Dashboard from "./subcomponents/Dashboard";
import DashboardSmartCode from "./subcomponents/DashboardSmartCode";
import DashboardSwap from "./subcomponents/DashboardSwap";
import ModuleWallet from "./subcomponents/ModuleWallet";
import DashboardModuleBurn from "./subcomponents/DashboardModuleBurn";
import ImageUploader from './subcomponents/ImageUploader';
import Rewards from "./subcomponents/Rewards";
import { getTokenPrice } from '../utils';

import { Button as PrimeButton } from 'primereact/button';
import rocketFiAbi from "../contracts/rocketFiAbi";
import splashIcon from "../imgs/rocketfi_yellow_token.png";
import radarIcon from '../imgs/radar_blue_icon.png';
import refuelIcon from '../imgs/refuel_color_icon.png';
import disconnectIcon from '../imgs/RocketDisconnect_icon.png';

import { Message } from 'primereact/message';
import { Zoom } from '@mui/material';

const replacements = {
  "0x0Cab564F58AD72877F0bc3Cd52468860294c15a6": "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c",
  "0x904B4224acA463666E98f21cC8C54f8D423f6648": "0x7130d2A12B9BCbFAe4f2634d864A1Ee1Ce3Ead9c",
  "0xDfD077c9C2F488e5CFeD14EbBcb7B8E056334EbA": "0x2170Ed0880ac9A755fd29B2688956BD959F933F8",
  "0x3834303483f139c26bc282528aDee6b7Da103D54": "0x1D2F0da169ceB9fC7B3144628dB156f3F6c60dBE",
  "0x09b22c55436cb5F4F91306e632e0a5F0DD031BA8": "0x570a5d26f7765ecb712c0924e4de545b89fd43df",
  "0x2a5D78825Bf470e70f9B62F2BCE8483941c11132": "0x2859e4544C4bB03966803b044A93563Bd2D0DD4D",
  "0x2761c609f3DbA79FFb34d28ab8d9cc2FF381fD3A": "0x3ee2200efb3400fabb9aacf31297cbdd1d435d47",
  "0xb014728C841D8Ccca6C236fE04B274D206cc60D0": "0xba2ae424d960c26247dd6c32edc70b295c744c43",
  "0x58291c58A2c4AEc7Ad24BaA87988dFc39f3FBC25": "0x0E09FaBB73Bd3Ade0a17ECC321fD13a19e81cE82",
};


const containerStyle = {
  maxWidth: '1920px', // Adjust this value according to your preference
  margin: '0 auto', // Centers the container
  width: '100%', // Takes full width on smaller screens
};

export const Rocketfi = () => {
  const { library, account, active, } = useWeb3React();
  const { contracts } = useContext(ContractContext);
  const { data, setData } = useContext(DataContext);
  const [smartCodeData, setSmartCodeData] = useState({});

  const [dbError, setDbError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [bnbBalance, setBnbBalance] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const toast = useRef(null);

  const isWalletConnected = useCallback(() => account !== null, [account]);

  const rocketFiPrice = useMemo(() => getTokenPrice(data?.tokensList, "ROCKETFI"), [data?.tokensList]);
  const [walletVisible, setWalletVisible] = useState(false);
  const [ecoVisible, setEcoVisible] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [manualRefresh, setManualRefresh] = useState(false);
  // const [burnEvents, setBurnEvents] = useState([]);


  const createTokensList = useCallback((tokenPoolData, pricingData) => {
    
    return tokenPoolData.map((tokenData) => {
      if (!tokenData) return undefined;
      const tokenAddress = tokenData._tokenAddress.toLowerCase();
      const pricingToken = pricingData[tokenAddress];
      let tokenPrice = pricingToken ? pricingToken.usd : undefined;
      return {
        value: tokenData._tokenAddress,
        label: tokenData._symbol,
        rocketFiSubscribed: tokenData._shareCount / (10 ** 9),
        totalDistributed: tokenData._totalDistributed,
        decimals: tokenData._decimals,
        poolID: tokenData._poolID,
        unclaimedAmount: tokenData._unclaimedAmount,
        claimedAmount: tokenData._claimedAmount,
        routerAddress: "0x10ED43C718714eb63d5aA57B78B54704E256024E",
        address: tokenData._tokenAddress,
        price: tokenPrice,
        reflectionPoolAddress: tokenData._reflectionPoolAddress,
        balanceOfHolder: tokenData._balanceOfHolder,
        totalSupply: tokenData._totalSupply,
        name: tokenData._name,
        bnbPending: tokenData._bnbPending,
      };
    });
  }, []);


  useEffect(() => {
    if (isWalletConnected() && contracts) {
      getData();
      const timer = setInterval(getData, 120000);
      return () => clearInterval(timer);
    }
  }, [account, contracts]);


  useEffect(() => {
    if (data?.activePoolAddress && data?.tokenPoolDataRp && data?.tokenPoolData && data?.tokenPoolDataV2 && data?.tokenPoolDataV1 && data?.pricingData)
      getAdditionalData();

  }, [data?.activePoolAddress, data?.tokenPoolDataRp, data?.tokenPoolData, data?.tokenPoolDataV2, data?.tokenPoolDataV1, data?.pricingData]);

  useEffect(() => {
    if (dbError && toast) {
      toast.current.show({ severity: 'warn', summary: 'Warning Message', detail: errorMessage, life: 5000 }); // display the custom error message
    }
  }, [dbError, errorMessage]);

  useEffect(() => {
    if (!library) return;
    const web3 = new Web3(library.provider);
    web3.eth.getBalance(account).then((balance) => {
      //console.log("balance:", balance);
      setBnbBalance(balance);
    });
  }, [library, account, data]);

  useEffect(() => {
    // console.log("bnbBalance:", bnbBalance); 
  }, [bnbBalance]);



  const getTokenPoolDataPaginated = async (contract, account, batchSize = 100) => {
    try {
      const { 0: totalBatches } = await contract.methods.getTotalCalls(batchSize).call();
      const results = [];

      for (let page = 0; page < totalBatches; page++) {
        const batch = await contract.methods.getTokenPoolDataBatch(account, page).call();
        results.push(...batch);
      }

      return results;
    } catch (err) {
      console.error("getTokenPoolDataPaginated failed:", err);
      return [];
    }
  };



  //web3 calls
  const getPrimaryData = useCallback(async () => {

    //console.log("Fetching primary data from rocketFiOracle contracts...");
    const [_contractData, _tokenPoolData] = await Promise.all([
       getContractData(contracts.rp_rocketFiOracle, account),
      getTokenPoolDataPaginated(contracts.rp_rewardPacksOracle, account),
    ]);

    
    //console.log("activePoolAddress:", str1);

    //console.log("Fetching additional data from rocketFiOracleV1, rocketFiOracleV2, and rp_rocketFiOracle contracts...");
    // Fetch data from rocketFiOracleV1 and rocketFiOracleV2 in the background
    const [tokenPoolDataV1Res, contractDataV2Res, tokenPoolDataV2Res,
      contractDataRpRes, tokenPoolDataRpRes] = await Promise.allSettled([
      getTokenPoolData(contracts.rocketFiOracleV1, account),
      getContractData(contracts.rocketFiOracleV2, account),
      getTokenPoolData(contracts.rocketFiOracleV2, account),
      getContractData(contracts.rp_rocketFiOracle, account),
      getTokenPoolDataPaginated(contracts.rp_rewardPacksOracle, account),
    ]);

    //console.log("Additional data fetch complete.");
    const tokenPoolDataV1 = tokenPoolDataV1Res.status === 'fulfilled' ? tokenPoolDataV1Res.value : [];
    const contractDataV2 = contractDataV2Res.status === 'fulfilled' ? contractDataV2Res.value : [];
    const tokenPoolDataV2 = tokenPoolDataV2Res.status === 'fulfilled' ? tokenPoolDataV2Res.value : [];
    const contractDataRp = contractDataRpRes.status === 'fulfilled' ? contractDataRpRes.value : [];
    const tokenPoolDataRp = tokenPoolDataRpRes.status === 'fulfilled' ? tokenPoolDataRpRes.value : [];


    const activePoolAddress = [
      ...new Set(
        tokenPoolDataRp.map((obj) => obj._tokenAddress)
      ),
    ];

    const str1 = activePoolAddress.toString();

    return {
      contractData: _contractData,
      tokenPoolData: _tokenPoolData,
      activePoolAddress: str1,
      tokenPoolDataV1,
      contractDataV2,
      tokenPoolDataV2,
      contractDataRp,
      tokenPoolDataRp,
    };
  }, [contracts, account]);

  //db calls
  const getDatabaseData = useCallback(async (_url, _endPoint, _parms) => {
    const data = await fetch(_url + _endPoint + "?" + _parms, {
      method: "GET",
      headers: {
        //"x-floatie-api-key" and secret kept as before
        "x-floatie-api-key": "hrNqBjEIOaMLvY7SNXQtYOca5LvlQPn6O3oduqOwg3L3up20",
        "x-floatie-api-secret": "O3Ux1HsMC8Qcd0rb",
        "Content-Type": "application/json",
      },
    });

    var jsondata = await data.json();
    return jsondata.result;
  }, []);

  const getSecondaryData = useCallback(async (activePoolAddress, _tokenPoolDataRp, _tokenPoolData, _tokenPoolDataV2, _tokenPoolDataV1, pricingData) => {
    // Normalize activePoolAddress to an array of addresses
    let addresses = [];
    if (!activePoolAddress) addresses = [];
    else if (Array.isArray(activePoolAddress)) addresses = activePoolAddress.slice();
    else if (typeof activePoolAddress === "string") addresses = activePoolAddress.split(",").map(a => a.trim()).filter(Boolean);
    // ensure unique
    addresses = [...new Set(addresses)];

    if (addresses.length === 0) {
      setErrorMessage("No active pool addresses available");
      setDbError(true);
      return;
    }

    // Chunk addresses into batches of max 100
    const chunkSize = 100;
    const chunks = [];
    for (let i = 0; i < addresses.length; i += chunkSize) {
      chunks.push(addresses.slice(i, i + chunkSize));
    }

    // Fire all fetches and wait for all to complete
    const fetchPromises = chunks.map((chunk) =>
      getDatabaseData("https://api.rocketfi.app/v1/", "tokens", "addresses=" + chunk.join(",") + "&limit=" + chunk.length)
    );

    const settled = await Promise.allSettled(fetchPromises);

    // Collect successful results
    const dbResults = settled
      .filter(r => r.status === "fulfilled" && Array.isArray(r.value))
      .map(r => r.value)
      .flat();

    if (!dbResults || dbResults.length === 0) {
      setErrorMessage("Database did not return any data"); // set custom error message when _dbData doesn't return anything
      setDbError(true);
      return;
    } else {
      // Clear previous DB error if any
      setDbError(false);
      setErrorMessage("");

      const _tokensList = createTokensList(_tokenPoolData, pricingData);
      const _tokensListV2 = createTokensList(_tokenPoolDataV2, pricingData);
      const _tokensListV1 = createTokensList(_tokenPoolDataV1, pricingData);
      const _tokensListRp = createTokensList(_tokenPoolDataRp, pricingData);

      return {
        dbData: dbResults,
        tokensList: _tokensList,
        tokensListV3: _tokensList,
        tokensListV2: _tokensListV2,
        tokensListV1: _tokensListV1,
        tokensListRp: _tokensListRp,
      };
    }
  }, [contracts, data, createTokensList, getDatabaseData]);

    const getPricingData = useCallback(async () => {
      const oracle = contracts.liquidityOraclePaginator;
      const BATCH_SIZE = 100;
      let pricingData = {};
    
      try {
        const { 0: totalBatches } = await oracle.methods.getActivePoolTotalCalls(BATCH_SIZE).call();
    
        for (let page = 0; page < totalBatches; page++) {
          const bnbBatch = await oracle.methods.getAllSmartRewardsBnbValuesBatch(page, BATCH_SIZE).call();
          const usdBatch = await oracle.methods.getAllSmartRewardsUsdValuesBatch(page, BATCH_SIZE).call();
    
          for (let i = 0; i < bnbBatch[0].length; i++) {
            const addr = bnbBatch[0][i].toLowerCase();
            const bnb = Number(bnbBatch[1][i]) / 1e18;
            const usdIndex = usdBatch[0].findIndex(x => x.toLowerCase() === addr);
            const usd = usdIndex !== -1 ? Number(usdBatch[1][usdIndex]) / 1e18 : 0;
    
            pricingData[addr] = { bnb, usd };
          }
        }
    
        // Hardcoded USD pegs
        pricingData["0x2e3b6906b976ab8da82228e0569b3ef6ea1e8cc6"] = { usd: 1.0 };
        pricingData["0x27874a586bafaf54d4cd0aae368ad9569d06d115"] = { usd: 1.0 };
    
        Object.entries(replacements).forEach(([original, alternate]) => {
          const orig = original.toLowerCase();
          const repl = alternate.toLowerCase();
          if (pricingData[orig] && pricingData[repl]) {
            pricingData[orig] = { ...pricingData[repl] };
          }
        });
    
        return { pricingData };
      } catch (err) {
        console.error("Paginated getPricingData failed:", err);
        return { pricingData };
      }
    }, [contracts]);


  const getData = useCallback(async () => {
    setIsRefreshing(true);
    if (!isWalletConnected() || !contracts || !contracts.rp_rocketFiOracle) {
      //console.error("Please connect to a wallet before interacting with the contract.");
      return;
    }

    try {
      // get pricing data
      const pricingData = await getPricingData();
      const primaryData = await getPrimaryData();

      const newData = {
        ...data,
        ...primaryData,
        ...pricingData,
      };

      setData(newData); // set the data state only once with the updated data
    } catch (err) {
      console.error("An error occurred while fetching contract data:", err);
    }
  }, [contracts, account]);

  const getAdditionalData = useCallback(async () => {
    if (!isWalletConnected() || !contracts || !contracts.rp_rocketFiOracle) {
      //console.error("Please connect to a wallet before interacting with the contract.");
      return;
    }

    try {
      const secondaryData = await getSecondaryData(data.activePoolAddress, data.tokenPoolDataRp, data.tokenPoolData, data.tokenPoolDataV2, data.tokenPoolDataV1, data.pricingData);
      //console.log("secondaryData:", secondaryData);
      const newData = {
        ...data,
        ...secondaryData,
      };

      setData(newData); // set the data state only once with the updated data
    } catch (err) {
      console.error("An error occurred while fetching contract data:", err);
    }
    if (manualRefresh) {
      //GetPastBurnEvents();
      setManualRefresh(false);

      setTimeout(() => {
        toast.current.show({ severity: 'success', summary: 'Data Refreshed', detail: new Date().toLocaleString(), life: 2500 });
        setIsRefreshing(false);
      }, 250);
    }
    else {
      setIsRefreshing(false);
    }

  }, [contracts, account, data]);

  const getContractData = useCallback(async (contract, account) => {
    return await contract.methods.getRocketData(account).call();
  } , []);

  const getTokenPoolData = useCallback(async (contract, account) => {
    return await contract.methods.getTokenPoolData(account).call();
  } , []);

  const getTokenBnbValueSmartRewards = useCallback(async (contract, token) => {
    return await contract.methods.getTokenBnbValueSmartRewards(token.address, true, true).call();
  } , []);

  const getAllSmartRewardsBnbValues = useCallback(async (contract) => {
    return await contract.methods.getAllSmartRewardsBnbValues().call();
  } , []);

  const getAllSmartRewardsUsdValues = useCallback(async (contract) => {
    return await contract.methods.getAllSmartRewardsUsdValues().call();
  } , []);

  const getSmartCodeData = useCallback(async () => {
    const result = await contracts.smartCodes.methods.smartCodeData(account).call();
    //console.log("getSmartCodeData:", result);
    return result;
  }, [contracts, account]);

  

  useEffect(() => {
    const fetchSmartCodeData = async () => {
      const result = await getSmartCodeData();
      setSmartCodeData(result);
    };

    if (data && contracts.smartCodes) {
      fetchSmartCodeData();
    }
  }, [data, contracts]);



  return (
    <div style={containerStyle}>
    {!(active) ?

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginTop: '20px', marginLeft: '20px', marginRight: '20px' }}>
        <RenderSplashScreen />
        {/* <Card title={<div style={cardOutlineHeaderStyle}>Welcome to the</div>} style={cardOutlineStyle}>
          <div style={infoOutlineStyle}>DeFi Reward Hub (BETA)</div>
          <>POWERED BY ROCKETFI</>
          <RenderSplashScreen />
        </Card> */}
      </div> :
      <>
        <Toast ref={toast} />
        {!walletVisible && <PrimeButton icon="pi pi-wallet" onClick={() => { setWalletVisible(true) }} rounded severity="secondary" aria-label="Wallet"
          style={{
            position: 'fixed', right: '22px', bottom: '22px',
            padding: '25px',
            //backgroundColor: '#f8f9fa',
            backgroundColor: '#4f4f4f',
            zIndex: '9999',
          }}
          size="large"
        ></PrimeButton>}
        {data && <PrimeButton icon={isRefreshing ? "pi pi-spin pi-refresh" : "pi pi-refresh"}
          onClick={() => {
            setManualRefresh(true);
            getData();
          }} rounded severity="secondary" aria-label="Refresh Data"
          disabled={isRefreshing}
          style={{
            position: 'fixed', right: '22px', bottom: '80px',
            padding: '25px',
            //backgroundColor: '#f8f9fa',
            backgroundColor: '#4f4f4f',
            zIndex: '9999',
          }}
          size="large"
        ></PrimeButton>}
        <Dialog visible={walletVisible} modal={false} onHide={() => setWalletVisible(false)}
          style={{ width: '35vw' }} maximizable breakpoints={{ '960px': '75vw', '641px': '100vw' }}
          header={"Wallet Summary"
          }
        >

          <ModuleWallet data={data} getData={getData} bnbBalance={bnbBalance} />
        </Dialog>


        <TabView className="my-tabview" activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
          <TabPanel header="Dashboard">
            <Dashboard data={data} getData={getData} account={account} rocketFiPrice={rocketFiPrice} smartCodeData={smartCodeData} />
          </TabPanel>
          <TabPanel header="Burn">
            <DashboardModuleBurn data={data} getData={getData} />
          </TabPanel>
          <TabPanel header="Rewards" >

            <Rewards data={data} getData={getData} />

          </TabPanel>
          <TabPanel header="Codes" >


            {/* <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px', marginTop: '10px' }}> */}
            <DashboardSmartCode data={data} getData={getData} rocketFiPrice={rocketFiPrice} />
            {/* </div> */}
          </TabPanel>
          <TabPanel header="Swap" >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px', marginTop: '10px' }}>
              <DashboardSwap data={data} getData={getData} bnbBalance={bnbBalance} smartCodeData={smartCodeData}/>
            </div>
          </TabPanel>
           
          {/* { account && (account=="0x5B51a16de51a7CEd3a01735Ba03FaaC7171275f9" || account == "0x7a4084446417AB470D5e542c0f722Cf91cbE77a7" ||
           account == "0xBdf3c6073Af427b42c0DFa4bf29107a2Cbd0923a" */}
          { account && (account=="0x5B51a16de51a7CEd3a01735Ba03FaaC7171275f9" || account=="0x3a5EE5cCD74Dd63d8129E0BC7AC207afdbc592AE" || account == "0x08Ccd6843BFD6C5634a643D0d3BA2E37768a35Df" || account == "0x5deb1df496452a07258708026bf9117e2248a2b2"
         ) && <TabPanel  header="Image Uploader" >
            <ImageUploader/>
          </TabPanel>}
        </TabView>

      </>}
      </div>
  );
};

export default React.memo(Rocketfi);



const RenderSplashScreen = () => {
  const styles = {
    splashContainer: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start', // Changed to flex-start to align items to the top
      alignItems: 'center',
      background: '#333333',
      borderRadius: '24px',
      padding: '40px 20px', // Increased vertical padding
      boxSizing: 'border-box',
      position: 'relative', // Added to position elements absolutely inside
    },
    header: {
      color: '#F8F8F8',
      fontSize: '1.8em',
      //fontFamily: 'Inter',
      fontWeight: '700',
      textAlign: 'center',
      wordWrap: 'break-word',
      marginBottom: '40px', // Increased margin
      maxWidth: '80%', // Ensure the header text doesn't span the full width
    },
    stepContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start', // Changed to flex-start to align items to the left
      width: '100%',
      gap: '20px', // Increased gap for better spacing
      //marginBottom: '40px', // Increased margin
    },
    stepTitle: {
      color: 'rgba(247, 247, 247, 0.4)',
      fontSize: '2.2em',
      //fontFamily: 'Inter',
      fontWeight: '700',
      alignSelf: 'flex-start', // Align to the start of the flex container
    },
    stepContent: {
      display: 'flex',
      alignItems: 'center', // Adjusted for vertical alignment
      flexDirection: 'row',
      gap: '10px',
    },
    stepIcon: {
      width: '45px',
      height: '45px',
      position: 'absolute',
      top: '0', // Align icons to the top of the overlayIcons div
    },
    stepText: {
      color: '#F8F8F8',
      fontSize: '1.1em',
      //fontFamily: 'Inter',
      fontWeight: '400',
      textAlign: 'left', // Align text to the left
    },
    overlayIcons: {
      display: 'flex',
      position: 'relative',
      // You might want to adjust the width to prevent the icons from overlapping the text
      width: '120px', // Width should be the total width of the three icons considering their overlap
      height: '45px', // Set the height to that of the icons to prevent overlap with other elements
      justifyContent: 'flex-start', // Aligns the icons to the start
    },
    step1Icon: {
      width: '45px',
      height: '45px',
      position: 'relative', // or 'static' if you don't need any special positioning
      // Other styles specific to step 1 icon
    },
    step2Icon: {
      width: '45px',
      height: '45px',
      position: 'absolute',
      top: '0', // Align icons to the top of the overlayIcons div
    },
    list: {
      listStyle: 'none',
      paddingLeft: '0px', // Set padding to 0 for custom indentation
    },
    listItem: {
      position: 'relative', // Positioned relatively to use absolute for pseudo-elements
      //paddingLeft: '1em', // Space for the bullet
      marginBottom: '0.5em', // Space between list items
      '&::before': {
        content: '"•"', // Unicode character for bullet
        color: '#FBAF00', // Custom color for the bullet, match your design
        position: 'absolute', // Positioned absolutely to place it on the left of the item
        left: '0', // Align to the left
      },
      color: '#F8F8F8',
      fontSize: '1.1em',
      //fontFamily: 'Inter',
      fontWeight: '400',
    },
    listItemBold: {
      position: 'relative', // Positioned relatively to use absolute for pseudo-elements
      paddingLeft: '1em', // Space for the bullet
      marginBottom: '0.5em', // Space between list items
      '&::before': {
        content: '"•"', // Unicode character for bullet
        color: '#FBAF00', // Custom color for the bullet, match your design
        position: 'absolute', // Positioned absolutely to place it on the left of the item
        left: '0', // Align to the left
      },
      color: '#F8F8F8',
      fontSize: '1.1em',
      //fontFamily: 'Inter',
      fontWeight: '800',
    },
    dashboardAccess: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start', // This aligns items to the left
      flexWrap: 'nowrap', // Prevents the items from wrapping onto the next line
      gap: '10px', // This adds some space between the icons and the text
    },
    radarIcon: {
      left: '0',
      zIndex: '3',
    },
    refuelIcon: {
      left: '20px',
      zIndex: '2',
    },
    splashIcon: {
      left: '40px',
      zIndex: '1',
    },
  };

  return (
    <Zoom in={true} >
    <div style={styles.splashContainer}>
      <img src={splashIcon} alt="RocketFi" style={{ width: '90px', marginBottom: '20px' }} />
      <div style={styles.header}>Welcome To RocketFi</div>

      <div>
        <div style={styles.stepContainer}>
          <div style={styles.stepTitle}>STEP 1</div>
          <div style={styles.stepContent}>
            <div style={styles.step1Icon}>
              <div style={{ ...styles.step1Icon, backgroundColor: '#FFE37B', borderRadius: '50%' }} />
              <img src={disconnectIcon} alt="Connect Wallet" style={{ width: '25px', height: '25px', position: 'absolute', left: '10px', top: '10px' }} />
            </div>
            <div style={styles.stepText}>Press “Tap To Connect” button on top right.</div>
          </div>
        </div>

        <div style={{...styles.stepContainer, marginTop: '40px'}}>
          <div style={styles.stepTitle}>STEP 2</div>
          <div style={styles.dashboardAccess}> {/* This div wraps the icons and the text */}
            <div style={styles.overlayIcons}>
              <img src={radarIcon} alt="Radar" style={{ ...styles.step2Icon, ...styles.radarIcon }} />
              <img src={refuelIcon} alt="Refuel" style={{ ...styles.step2Icon, ...styles.refuelIcon }} />
              <img src={splashIcon} alt="RocketFi" style={{ ...styles.step2Icon, ...styles.splashIcon }} />
            </div>
            <div style={styles.stepText}>Get Access To Dashboard + Tools</div>
          </div>
          <ul style={styles.list}>
            <li><span style={styles.listItemBold}>• Customize</span><span style={styles.listItem}> Rewards</span></li>
            <li><span style={styles.listItemBold}>• Create</span><span style={styles.listItem}> Smart Codes</span></li>
            <li><span style={styles.listItemBold}>• Reward</span><span style={styles.listItem}> Insights</span></li>
            <li><span style={styles.listItemBold}>• Gamified</span><span style={styles.listItem}> Reward Pools</span></li>
          </ul>
        </div>

      </div>
    </div>
    </Zoom>
  );
};



